import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../config/api';

const CartContext = createContext(null);

/**
 * PROFESSIONAL E-COMMERCE CART SYSTEM
 * 
 * Architecture:
 * - Frontend: Handles UX, displays cart, manages state
 * - Backend: Source of truth, validates everything, manages database
 * 
 * Flow:
 * 1. User clicks "Add to Cart" → Frontend sends product_id + quantity
 * 2. Backend validates product, checks stock, gets real price
 * 3. Backend saves to database and returns updated cart
 * 4. Frontend updates UI with backend response
 * 
 * Guest users: Cart saved in localStorage, synced to DB on login
 * Logged-in users: Cart saved directly to database
 */

export const CartProvider = ({ children }) => {
  const { user, session } = useAuth();
  const location = useLocation();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);

  // Check if we're on a staff/admin/manager route (staff can't shop)
  const isStaffRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/manager') || location.pathname.startsWith('/staff');

  // ============================================
  // LOAD CART ON MOUNT OR LOGIN/LOGOUT
  // ============================================
  
  useEffect(() => {
    if (isStaffRoute) {
      setCart([]);
      return;
    }

    if (user && session) {
      // Logged in - Process pending cart item first, then sync
      const handleLogin = async () => {
        // Check if there's a pending item to add (from "Add to Cart" before login)
        const pendingItem = localStorage.getItem('brightbazaar_pending_cart_item');
        
        if (pendingItem) {
          // Process the pending item (this will also load the cart)
          await processPendingCartItem();
        } else {
          // Check if there's a localStorage cart to sync
          const localCart = localStorage.getItem('brightbazaar_cart');
          if (localCart) {
            // User has items from when they were a guest
            syncGuestCartToDatabase();
          } else {
            // Just load from database
            loadCartFromDatabase();
          }
        }
      };
      
      handleLogin();
    } else {
      // Guest - Cart will be handled by redirect to login on Add to Cart
      // We don't show guest carts anymore
      setCart([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session, isStaffRoute]);

  // ============================================
  // LOAD FUNCTIONS
  // ============================================

  const loadCartFromLocalStorage = () => {
    try {
      const savedCart = localStorage.getItem('brightbazaar_cart');
      setCart(savedCart ? JSON.parse(savedCart) : []);
    } catch (err) {
      console.error('Error loading cart from localStorage:', err);
      setCart([]);
    }
  };

  const loadCartFromDatabase = async () => {
    if (!session) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/cart`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        // Don't show errors for auth issues
        if (response.status === 401 || response.status === 403) {
          setCart([]);
          return;
        }
        throw new Error('Failed to load cart');
      }

      const data = await response.json();
      
      if (data.success) {
        setCart(data.cart || []);
      }
    } catch (err) {
      console.error('Error loading cart from database:', err);
      setCart([]);
    } finally {
      setLoading(false);
    }
  };

  const syncGuestCartToDatabase = async () => {
    if (!session) return;

    try {
      setLoading(true);
      const localCart = localStorage.getItem('brightbazaar_cart');
      const guestItems = localCart ? JSON.parse(localCart) : [];

      if (guestItems.length === 0) {
        await loadCartFromDatabase();
        return;
      }

      console.log(`🔄 Syncing ${guestItems.length} guest cart items...`);

      // Add each item to database via backend API
      // Backend will validate and set correct prices
      for (const item of guestItems) {
        try {
          await fetch(`${API_URL}/api/cart`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              product_id: item.product_id || item.id,
              variant_id: item.variant_id || null,
              quantity: item.quantity || 1
            })
          });
        } catch (itemErr) {
          console.error(`Failed to sync item ${item.id}:`, itemErr);
          // Continue with other items
        }
      }

      // Clear localStorage after sync
      localStorage.removeItem('brightbazaar_cart');
      
      // Load the now-synced cart from database
      await loadCartFromDatabase();
      
      console.log('✅ Guest cart synced successfully');
    } catch (err) {
      console.error('Error syncing guest cart:', err);
      await loadCartFromDatabase();
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // ADD TO CART (Main Function)
  // ============================================

  const addToCart = async (product, variant = null) => {
    // Validate input
    if (!product || !product.id) {
      console.error('Invalid product data:', product);
      return { success: false, error: 'Invalid product data' };
    }

    // Guest users - redirect to login immediately (NO POPUP)
    if (!user || !session) {
      // Save product info for post-login addition
      const pendingCartItem = {
        product,
        variant,
        timestamp: Date.now()
      };
      localStorage.setItem('brightbazaar_pending_cart_item', JSON.stringify(pendingCartItem));
      
      // Save current page for redirect back after login
      localStorage.setItem('brightbazaar_redirect_after_login', window.location.pathname);
      
      // Immediate redirect to login - NO ALERT/POPUP
      window.location.href = '/login';
      return { success: false, redirected: true };
    }

    // Logged-in users - send to backend
    try {
      setLoading(true);
      
      const response = await fetch(`${API_URL}/api/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          product_id: product.id,
          variant_id: variant?.id || null,
          quantity: 1
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        // Handle specific error codes
        if (data.code === 'ADMIN_CANNOT_SHOP') {
          return { 
            success: false, 
            error: 'Admin users cannot add items to cart. Please login as a customer to shop.',
            code: 'ADMIN_CANNOT_SHOP'
          };
        } else if (data.code === 'INSUFFICIENT_STOCK') {
          return { 
            success: false, 
            error: `Only ${data.available_stock} units available in stock`,
            code: 'INSUFFICIENT_STOCK',
            availableStock: data.available_stock
          };
        } else if (data.code === 'PRODUCT_INACTIVE') {
          return { 
            success: false, 
            error: 'This product is no longer available',
            code: 'PRODUCT_INACTIVE'
          };
        } else {
          return { 
            success: false, 
            error: data.error || 'Failed to add item to cart'
          };
        }
      }

      // Success! Reload cart from database to get updated state
      await loadCartFromDatabase();
      
      return { 
        success: true, 
        action: data.action,
        message: data.action === 'updated' ? 'Quantity updated!' : 'Added to cart!'
      };
      
    } catch (err) {
      console.error('Error adding to cart:', err);
      return { 
        success: false, 
        error: 'Unable to add product to cart. Please try again.'
      };
    } finally {
      setLoading(false);
    }
  };

  // Process pending cart item after login
  const processPendingCartItem = async () => {
    const pendingItem = localStorage.getItem('brightbazaar_pending_cart_item');
    
    if (pendingItem && user && session) {
      try {
        const { product, variant } = JSON.parse(pendingItem);
        
        // Clear the pending item immediately to prevent reprocessing
        localStorage.removeItem('brightbazaar_pending_cart_item');
        
        console.log('📦 Processing pending cart item after login:', product.name);
        
        // Add the item to cart
        const result = await addToCart(product, variant);
        
        if (result.success) {
          console.log('✅ Pending item added successfully');
        } else {
          console.error('❌ Failed to add pending item:', result.error);
        }
        
        return result;
      } catch (err) {
        console.error('Error processing pending cart item:', err);
        localStorage.removeItem('brightbazaar_pending_cart_item');
      }
    }
    
    return null;
  };

  // ============================================
  // UPDATE QUANTITY
  // ============================================

  const updateQuantity = async (cartItemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(cartItemId);
      return;
    }

    if (user && session) {
      // Logged-in user - update in database
      try {
        const cartItem = cart.find(item => 
          item.cartItemId === cartItemId || item.db_cart_item_id === cartItemId
        );
        
        if (!cartItem || !cartItem.db_cart_item_id) {
          console.error('Cart item not found');
          return;
        }

        setLoading(true);
        const response = await fetch(`${API_URL}/api/cart/${cartItem.db_cart_item_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ quantity })
        });

        if (response.ok) {
          await loadCartFromDatabase();
        }
      } catch (err) {
        console.error('Error updating quantity:', err);
      } finally {
        setLoading(false);
      }
    } else {
      // Guest - update localStorage
      const updatedCart = cart.map(item =>
        item.cartItemId === cartItemId ? { ...item, quantity } : item
      );
      setCart(updatedCart);
      localStorage.setItem('brightbazaar_cart', JSON.stringify(updatedCart));
    }
  };

  // ============================================
  // REMOVE FROM CART
  // ============================================

  const removeFromCart = async (cartItemId) => {
    if (user && session) {
      // Logged-in user - remove from database
      try {
        const cartItem = cart.find(item => 
          item.cartItemId === cartItemId || item.db_cart_item_id === cartItemId
        );
        
        if (!cartItem || !cartItem.db_cart_item_id) {
          console.error('Cart item not found');
          return;
        }

        setLoading(true);
        const response = await fetch(`${API_URL}/api/cart/${cartItem.db_cart_item_id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (response.ok) {
          await loadCartFromDatabase();
        }
      } catch (err) {
        console.error('Error removing from cart:', err);
      } finally {
        setLoading(false);
      }
    } else {
      // Guest - remove from localStorage
      const updatedCart = cart.filter(item => item.cartItemId !== cartItemId);
      setCart(updatedCart);
      localStorage.setItem('brightbazaar_cart', JSON.stringify(updatedCart));
    }
  };

  // ============================================
  // CLEAR CART
  // ============================================

  const clearCart = async () => {
    if (user && session) {
      // Logged-in user - clear database
      try {
        setLoading(true);
        await fetch(`${API_URL}/api/cart`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        setCart([]);
      } catch (err) {
        console.error('Error clearing cart:', err);
      } finally {
        setLoading(false);
      }
    } else {
      // Guest - clear localStorage
      setCart([]);
      localStorage.removeItem('brightbazaar_cart');
    }
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const price = item.price || 0;
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + (item.quantity || 0), 0);
  };

  // ============================================
  // PROVIDER
  // ============================================

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        loading,
        isLoggedIn: !!user,
        processPendingCartItem // Export for use in Login component if needed
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
